import React, {useMemo, useRef, useState} from "react";
import styled from "styled-components";
import {InventoryQuantity, useInventory} from "../State/InventoryContext";
import {InventoryItem} from "../State/Inventory";
import {ResourceTooltip} from "../SharedComponents/ResourceTooltip";
import {findSetupsUsingResource} from "../Assembly/AssemblyHelpers";

const I = {
    Panel: styled.div`
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 20px 16px;
      background: linear-gradient(180deg, #e9f5ff 0%, #f7fbff 100%);
      border-right: 1px solid #d0e2f2;
      height: 100vh;
      box-sizing: border-box;
      width: 240px;
    `,
    Header: styled.div`
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: bold;
      color: #0b3d91;
    `,
    ResetButton: styled.button`
      border: 1px solid #b0c4de;
      background-color: #fff;
      color: #0b3d91;
      border-radius: 6px;
      padding: 6px 10px;
      cursor: pointer;
      font-size: 12px;
    `,
    Section: styled.div`
      display: flex;
      flex-direction: column;
      gap: 6px;
    `,
    SectionTitle: styled.div`
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #2c5282;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    `,
    SectionDot: styled.span<{ $color: string }>`
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: ${props => props.$color};
      display: inline-block;
    `,
    Items: styled.div`
      display: grid;
      grid-template-columns: repeat(1, minmax(0, 1fr));
      gap: 6px;
    `,
    ItemRow: styled.div`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 6px 8px;
      background-color: #ffffff;
      border: 1px solid #e2ecf7;
      border-radius: 8px;
      box-shadow: 0 1px 2px rgba(10, 31, 68, 0.08);
    `,
    ItemInfo: styled.div`
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    `,
    ItemIcon: styled.div<{ $color: string }>`
      width: 26px;
      height: 26px;
      border-radius: 8px;
      background: ${props => props.$color};
      color: #0b2545;
      font-weight: 700;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
    `,
    ItemName: styled.span`
      font-size: 13px;
      color: #12355b;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
    ItemCount: styled.span`
      font-weight: 700;
      color: #0b3d91;
    `,
};

type CategoryKey = InventoryItem["category"];

type CategoryDefinition = {
    key: CategoryKey;
    label: string;
    color: string;
};

const CATEGORY_ORDER: CategoryDefinition[] = [
    {key: "resource", label: "Resources", color: "#fcd34d"},
    {key: "basicMaterial", label: "Basic Materials", color: "#93c5fd"},
    {key: "advancedMaterial", label: "Advanced Materials", color: "#7dd3fc"},
    {key: "basicBuilding", label: "Basic Buildings", color: "#c4f1f9"},
    {key: "advancedBuilding", label: "Advanced Buildings", color: "#99f6e4"},
    {key: "powerBuilding", label: "Power Buildings", color: "#fde68a"},
    {key: "scienceBuilding", label: "Science Buildings", color: "#a7f3d0"},
    {key: "science", label: "Science", color: "#fbcfe8"},
    {key: "module", label: "Modules", color: "#ddd6fe"},
];

function ItemIcon({name, color}: { name: string, color: string }) {
    const letter = name.charAt(0).toUpperCase();
    return <I.ItemIcon $color={color}>{letter}</I.ItemIcon>;
}

function InventoryRow({itemq, color}: { itemq: InventoryQuantity, color: string }) {
    const {inventory} = useInventory();
    const [showTooltip, setShowTooltip] = useState(false);
    const [delayTimer, setDelayTimer] = useState<number | null>(null);
    const rowRef = useRef<HTMLDivElement>(null);

    const matchingSetups = useMemo(
        () => findSetupsUsingResource(itemq.item.id, inventory.assemblySetups),
        [itemq.item.id, inventory.assemblySetups]
    );

    const handleMouseEnter = () => {
        const timer = window.setTimeout(() => {
            setShowTooltip(true);
        }, 200);
        setDelayTimer(timer);
    };

    const handleMouseLeave = () => {
        if (delayTimer) {
            clearTimeout(delayTimer);
            setDelayTimer(null);
        }
        setShowTooltip(false);
    };

    return <>
        <I.ItemRow
            ref={rowRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <I.ItemInfo>
                <ItemIcon name={itemq.item.name} color={color} />
                <I.ItemName title={itemq.item.name}>{itemq.item.name}</I.ItemName>
            </I.ItemInfo>
            <I.ItemCount>{itemq.quantity}</I.ItemCount>
        </I.ItemRow>
        {showTooltip && matchingSetups.length > 0 && (
            <ResourceTooltip
                resourceId={itemq.item.id}
                resourceName={itemq.item.name}
                assemblySetups={matchingSetups}
                anchorRef={rowRef}
                onClose={() => setShowTooltip(false)}
            />
        )}
    </>;
}

export function Inventory() {
    const {inventory, resetInventory} = useInventory();

    const groupedInventory = useMemo(() => {
        const byCategory: Record<CategoryKey, InventoryQuantity[]> = {
            resource: [],
            basicMaterial: [],
            basicBuilding: [],
            scienceBuilding: [],
            science: [],
            advancedMaterial: [],
            advancedBuilding: [],
            powerBuilding: [],
            module: [],
        };
        inventory.inventory.forEach(itemQ => {
            byCategory[itemQ.item.category]?.push(itemQ);
        });
        return byCategory;
    }, [inventory.inventory]);

    return <I.Panel>
        <I.Header>
            <span>Inventory</span>
            <I.ResetButton onClick={() => {
                resetInventory();
                window.location.reload();
            }}>Reset Inventory</I.ResetButton>
        </I.Header>
        {CATEGORY_ORDER.map(category => {
            const items = groupedInventory[category.key] ?? [];
            if (!items.length) {
                return null;
            }
            return <I.Section key={category.key}>
                <I.SectionTitle>
                    <I.SectionDot $color={category.color} />
                    {category.label}
                </I.SectionTitle>
                <I.Items>
                    {items.map(itemQ => <InventoryRow key={itemQ.item.id} itemq={itemQ} color={category.color} />)}
                </I.Items>
            </I.Section>;
        })}
    </I.Panel>;
}
